'use client';

const timeSavings = [
  'No manual onboarding reviews',
  'No repetitive programme writing',
  'No chasing check-ins',
  'No spreadsheet analysis',
  'No formatting feedback',
];

const improvements = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    title: 'Early Red-Flag Detection',
    description: 'Catch at-risk clients before they drop off — every week, automatically.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    title: 'Consistent Client Communication',
    description: 'Every client gets the same structured, professional experience — every week.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    title: 'Professional Structured Reports',
    description: 'Deliver polished onboarding summaries and weekly reviews that reflect your standard.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Clear Weekly Performance Tracking',
    description: 'Trend data, compliance scores, and progress flags — all in one structured view.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    title: 'Increased Perceived Value',
    description: 'Clients feel monitored, supported, and invested in — which drives retention.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    title: 'Higher Client Retention',
    description: 'Structured systems keep clients engaged, accountable, and less likely to quit.',
  },
];

export default function BenefitsSection() {
  return (
    <>
      {/* Section 4: What This Saves */}
      <section className="py-24 md:py-32 px-5 md:px-8 bg-gradient-to-br from-primary via-primary/95 to-primary/90 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-5">
                <span className="text-xs font-bold text-white uppercase tracking-wider">What This Saves</span>
              </span>
              <h2 className="display-md text-white mb-5">
                Save 10–20+ Hours
                <br />
                Per Month.
              </h2>
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                For a 25-client coach, ElevateX eliminates the equivalent of a part-time admin role — without hiring.
              </p>
              <ul className="space-y-3">
                {timeSavings?.map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-white/80 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-white/90 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8">
              <p className="text-white/70 text-xs font-bold uppercase tracking-wider mb-6">For a 25-client coach</p>
              <div className="space-y-5">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-semibold text-sm">Onboarding Admin</span>
                    <span className="text-white/70 text-sm">~4 hrs/month</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: '80%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-semibold text-sm">Programme Writing</span>
                    <span className="text-white/70 text-sm">~6 hrs/month</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: '90%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-semibold text-sm">Check-In Reminders</span>
                    <span className="text-white/70 text-sm">~4 hrs/month</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: '70%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-semibold text-sm">Weekly Reviews</span>
                    <span className="text-white/70 text-sm">~4 hrs/month</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: '70%' }} />
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/20">
                <div className="flex justify-between items-center">
                  <span className="text-white font-bold">Total Saved</span>
                  <span className="text-white font-bold text-xl">18+ hrs/month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: What This Improves */}
      <section className="py-24 md:py-32 px-5 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-coral mb-4">What This Improves</span>
            <h2 className="display-md text-foreground mb-5">
              More Structure. Higher Retention.
              <br />
              <span className="gradient-text">Cleaner Delivery.</span>
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Clients feel monitored and supported. That's what keeps them.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {improvements?.map((item, index) => (
              <div
                key={index}
                className="bg-surface rounded-3xl p-7 border border-black/[0.07] hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                  {item?.icon}
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{item?.title}</h3>
                <p className="text-muted text-sm leading-relaxed">{item?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}